import { useContext, useMemo, useState } from 'react';
import { Dropdown, Tooltip } from 'antd';
import { CheckOutlined, DownOutlined, EnvironmentOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';

import useAuth from '../../../../hooks/useAuth';
import { ThemeContext } from '../../../../contexts/ThemeContext';
import { urlHttpGov } from '../../../../services/urls';
import { ICity } from '../../../../interfaces';
import { EmitSuccessAlert } from '../../../../utils/EmitSuccessAlert';
import { EmitError } from '../../../../utils/EmitError';


interface IProps {
    collapsed?: boolean;
}

export default function SidebarHeader({ collapsed = false }: IProps) {
    const { activeCity, user, setUserActiveCity } = useAuth();
    const { theme } = useContext(ThemeContext);
    const navigate = useNavigate();
    const [isSwitching, setIsSwitching] = useState(false);

    const canSwitch = Boolean(user?.cities && user.cities.length > 1);

    const logoFileName =
        activeCity?.logo_county ||
        (theme === 'light' ? activeCity?.logo_light : activeCity?.logo_dark);

    const logoSrc = activeCity && logoFileName
        ? `${urlHttpGov}/logo_county/${logoFileName}`
        : '';

    const handleSelectCity = async (targetCity: ICity) => {
        if (targetCity.id === activeCity?.id || isSwitching) {
            return;
        }

        try {
            setIsSwitching(true);
            await setUserActiveCity(targetCity);

            localStorage.removeItem('@NotaMunicipio:appUserFilters');
            localStorage.removeItem('@NotaMunicipio:invoicesCompanyByCity');
            localStorage.removeItem('@NotaMunicipio:allInvoices');

            navigate('/dashboard');
            EmitSuccessAlert(`Município alterado para ${targetCity.city} - ${targetCity.uf}`);
        } catch (err) {
            EmitError(err, true, 'sidebarHeader_switchCity');
        } finally {
            setIsSwitching(false);
        }
    };

    const cityMenuItems = useMemo(() => {
        if (!user?.cities || user.cities.length <= 1) return [];

        const sorted = [...user.cities].sort((a, b) =>
            `${a.city} - ${a.uf}`.localeCompare(`${b.city} - ${b.uf}`)
        );

        return sorted.map((c) => {
            const isSelected = c.id === activeCity?.id;
            return {
                key: String(c.id),
                label: (
                    <div
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 16,
                            padding: '4px 2px',
                            minWidth: 190,
                            fontWeight: isSelected ? 600 : 400,
                            color: isSelected ? '#1677ff' : undefined,
                        }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <EnvironmentOutlined
                                style={{
                                    fontSize: 13,
                                    color: isSelected ? '#1677ff' : '#94a3b8',
                                }}
                            />
                            <span>
                                {c.city} - {c.uf}
                            </span>
                        </div>
                        {isSelected && (
                            <CheckOutlined style={{ fontSize: 12, color: '#1677ff' }} />
                        )}
                    </div>
                ),
                onClick: () => handleSelectCity(c),
            };
        });
    }, [user?.cities, activeCity?.id, isSwitching]);

    if (!activeCity) {
        return null;
    }

    if (collapsed) {
        const collapsedLogoBox = (
            <div className="sidebar-brand-collapsed-box">
                <img
                    src={logoSrc}
                    alt={activeCity?.city || 'Logo'}
                    className="sidebar-brand-collapsed-img"
                />
            </div>
        );

        if (canSwitch) {
            return (
                <div className="sidebar-brand-collapsed-container">
                    <Dropdown
                        menu={{
                            items: cityMenuItems,
                            selectedKeys: [String(activeCity?.id)],
                        }}
                        trigger={['click']}
                        placement="bottomLeft"
                        overlayClassName="city-selector-dropdown"
                    >
                        <Tooltip
                            title={`Alterar município (${activeCity?.city} - ${activeCity?.uf})`}
                            placement="right"
                        >
                            {collapsedLogoBox}
                        </Tooltip>
                    </Dropdown>
                </div>
            );
        }

        return (
            <div className="sidebar-brand-collapsed-container">
                <Tooltip title={`${activeCity?.city} - ${activeCity?.uf}`} placement="right">
                    {collapsedLogoBox}
                </Tooltip>
            </div>
        );
    }

    return (
        <div className="sidebar-brand-expanded-container">

            <div className="sidebar-brand-logo-wrap">
                <img
                    src={logoSrc}
                    alt={activeCity?.city || 'Logo'}
                    className="sidebar-brand-logo-img"
                />
            </div>

            {canSwitch ? (
                <Dropdown
                    menu={{
                        items: cityMenuItems,
                        selectedKeys: [String(activeCity?.id)],
                    }}
                    trigger={['click']}
                    placement="bottomCenter"
                    overlayClassName="city-selector-dropdown"
                >
                    <div
                        className="city-selector-interactive-pill"
                        title="Clique para alternar o município"
                    >
                        <div className="city-selector-pill-left">
                            <EnvironmentOutlined className="city-selector-icon" />
                            <span className="city-selector-name">
                                {activeCity?.city} - {activeCity?.uf}
                            </span>
                        </div>
                        <DownOutlined className="city-selector-chevron" />
                    </div>
                </Dropdown>
            ) : (
                <div className="city-selector-static-badge">
                    <EnvironmentOutlined className="city-selector-icon-static" />
                    <span className="city-selector-name-static">
                        {activeCity?.city} - {activeCity?.uf}
                    </span>
                </div>
            )}
        </div>
    );
}
